import { useEffect, useState } from 'react';
import { GraduationCap } from 'lucide-react';
import dayjs, { fmtTime } from '../../lib/dates';
import { InstructorLesson, PlanningDay } from '../../types';
import { lessonState } from '../../lib/lessonStatus';
import { isOfferedOpenSlot } from '../../lib/openSlot';
import { cx } from '../../components/charte';

// Vue Calendrier du planning (refonte du 14/10/2026) : la semaine en grille, une colonne par jour, heures en
// ligne. Un toucher sur une leçon ouvre sa carte (mêmes actions que la liste). Sur téléphone, la grille garde
// sa largeur et défile de côté. Le dimanche n'est pas affiché (plus de place), sauf s'il porte un rendez-vous.

export interface CalendarAccompaniment {
  id: string;
  candidate_name: string;
  center: string;
  start: string;
  end: string;
}

/** Hauteur d'une heure dans la grille (px). */
const HOUR = 56;

const minutesOf = (iso: string) => {
  const d = dayjs(iso);
  return d.hour() * 60 + d.minute();
};

type Block =
  | { kind: 'lesson'; id: string; start: number; end: number; lesson: InstructorLesson }
  | { kind: 'exam'; id: string; start: number; end: number; exam: CalendarAccompaniment };

/** Rendez-vous qui se chevauchent : côte à côte (colonnes de même largeur). */
function layout(blocks: Block[]) {
  const sorted = [...blocks].sort((a, b) => a.start - b.start || b.end - a.end);
  const placed: Array<Block & { lane: number; lanes: number }> = [];
  let group: Array<Block & { lane: number; lanes: number }> = [];
  let groupEnd = -1;
  const flush = () => {
    const lanes = Math.max(1, ...group.map((g) => g.lane + 1));
    group.forEach((g) => (g.lanes = lanes));
    placed.push(...group);
    group = [];
  };
  for (const block of sorted) {
    if (block.start >= groupEnd && group.length) flush();
    const used = new Set(group.filter((g) => g.end > block.start).map((g) => g.lane));
    let lane = 0;
    while (used.has(lane)) lane += 1;
    group.push({ ...block, lane, lanes: 1 });
    groupEnd = Math.max(groupEnd, block.end);
  }
  if (group.length) flush();
  return placed;
}

function lessonTone(lesson: InstructorLesson) {
  if (lesson.isOpenSlot)
    return isOfferedOpenSlot(lesson)
      ? 'border-2 border-dashed border-emerald-300 bg-success-light/70 text-success-dark'
      : 'border-2 border-dashed border-line bg-surface-light text-secondary';
  const state = lessonState(lesson);
  if (state === 'honored') return 'bg-success-light text-success-dark';
  if (state === 'noshow' || state === 'absent') return 'bg-danger-light text-danger-dark';
  if (state === 'cancelled') return 'bg-surface-2 text-muted line-through';
  return 'bg-primary-100 text-primary-900';
}

function lessonTitle(lesson: InstructorLesson) {
  if (lesson.isOpenSlot) return isOfferedOpenSlot(lesson) ? 'Ouvert à la réservation' : 'Créneau non réservé';
  return lesson.candidate ? `${lesson.candidate.firstName} ${lesson.candidate.lastName}` : 'Élève';
}

export default function WeekCalendar({
  days,
  accompanimentsOf,
  onOpen,
}: {
  days: PlanningDay[];
  accompanimentsOf: (date: string) => CalendarAccompaniment[];
  onOpen: (lesson: InstructorLesson) => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const columns = days
    .map((day) => {
      const blocks: Block[] = [
        ...day.lessons.map((lesson) => ({
          kind: 'lesson' as const,
          id: lesson.id,
          start: minutesOf(lesson.startsAt),
          end: Math.max(minutesOf(lesson.endsAt), minutesOf(lesson.startsAt) + 20),
          lesson,
        })),
        ...accompanimentsOf(day.date).map((exam) => ({
          kind: 'exam' as const,
          id: exam.id,
          start: minutesOf(exam.start),
          end: Math.max(minutesOf(exam.end), minutesOf(exam.start) + 20),
          exam,
        })),
      ];
      return { day, blocks: layout(blocks) };
    })
    .filter(({ day, blocks }) => dayjs(day.date).day() !== 0 || blocks.length > 0);
  const grid = { gridTemplateColumns: `44px repeat(${columns.length}, minmax(0, 1fr))` };

  // Heures affichées : 7 h – 20 h, élargies si un rendez-vous déborde.
  const all = columns.flatMap((c) => c.blocks);
  const first = Math.min(7, ...all.map((b) => Math.floor(b.start / 60)));
  const last = Math.max(20, ...all.map((b) => Math.ceil(b.end / 60)));
  const hours = Array.from({ length: last - first }, (_, i) => first + i);
  const top = (minutes: number) => ((minutes - first * 60) / 60) * HOUR;
  const nowMinutes = minutesOf(new Date(now).toISOString());

  return (
    <div className="overflow-x-auto rounded-card bg-white shadow-card">
      <div className="min-w-[640px]">
        {/* En-têtes des jours */}
        <div className="grid border-b border-line" style={grid}>
          <span />
          {columns.map(({ day }) => {
            const today = dayjs(day.date).isSame(dayjs(now), 'day');
            return (
              <div key={day.date} className="px-1 py-2 text-center">
                <p className={cx('text-[11px] font-medium capitalize', today ? 'text-primary-600' : 'text-secondary')}>
                  {dayjs(day.date).format('ddd')}
                </p>
                <p
                  className={cx(
                    'mx-auto grid h-8 w-8 place-items-center rounded-full text-[15px] font-semibold tabular-nums',
                    today ? 'bg-primary text-white' : 'text-heading',
                  )}
                >
                  {dayjs(day.date).format('D')}
                </p>
              </div>
            );
          })}
        </div>

        {/* Grille */}
        <div className="relative grid" style={{ ...grid, height: hours.length * HOUR }}>
          <div className="relative">
            {hours.map((h, i) => (
              <span key={h} className="absolute right-1.5 -translate-y-1/2 text-[11px] tabular-nums text-muted" style={{ top: i * HOUR }}>
                {i === 0 ? '' : `${h}h`}
              </span>
            ))}
          </div>
          {columns.map(({ day, blocks }) => {
            const today = dayjs(day.date).isSame(dayjs(now), 'day');
            return (
              <div
                key={day.date}
                className={cx('relative border-l border-line', today && 'bg-primary-50/40')}
                aria-label={dayjs(day.date).format('dddd D MMMM')}
                role="group"
              >
                {hours.map((h, i) => (
                  <span key={h} className="absolute inset-x-0 border-t border-slate-100" style={{ top: i * HOUR }} aria-hidden />
                ))}
                {blocks.map((b) => {
                  const style = {
                    top: top(b.start) + 1,
                    height: Math.max(((b.end - b.start) / 60) * HOUR - 2, 22),
                    left: `calc(${(b.lane / b.lanes) * 100}% + 2px)`,
                    width: `calc(${100 / b.lanes}% - 4px)`,
                  };
                  if (b.kind === 'exam')
                    return (
                      <div
                        key={b.id}
                        style={style}
                        className="absolute overflow-hidden rounded-lg bg-primary px-1.5 py-1 text-[11px] leading-tight text-white"
                        title={`Accompagnement examen · ${b.exam.candidate_name} · ${b.exam.center}`}
                      >
                        <p className="flex items-center gap-1 font-semibold">
                          <GraduationCap size={11} aria-hidden />
                          {fmtTime(b.exam.start)}
                        </p>
                        <p className="truncate">Examen · {b.exam.candidate_name}</p>
                      </div>
                    );
                  const lesson = b.lesson;
                  const title = lessonTitle(lesson);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      style={style}
                      onClick={() => onOpen(lesson)}
                      aria-label={`${title}, ${fmtTime(lesson.startsAt)} – ${fmtTime(lesson.endsAt)}`}
                      className={cx(
                        'absolute overflow-hidden rounded-lg px-1.5 py-1 text-left text-[11px] leading-tight transition-shadow hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300',
                        lessonTone(lesson),
                      )}
                    >
                      <p className="font-semibold tabular-nums">{fmtTime(lesson.startsAt)}</p>
                      <p className="truncate font-medium">{title}</p>
                    </button>
                  );
                })}
                {today && nowMinutes >= first * 60 && nowMinutes <= last * 60 && (
                  <span className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-primary" style={{ top: top(nowMinutes) }} aria-hidden>
                    <span className="absolute -left-1 -top-[5px] h-2 w-2 rounded-full bg-primary" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
